import React, { useEffect, useMemo, useState } from 'react'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import API from '../../../services/API'

// A booking's quoting position, as returned by
// GET /pts/bookings/quote-statuses (best quote wins)
export const QUOTE_STATUS_DISPLAY = {
  Converted: { label: 'Quote Accepted', color: 'success', accepted: true },
  Accepted: { label: 'Quote Accepted', color: 'success', accepted: true },
  Sent: { label: 'Quote Pending', color: 'warning' },
  Draft: { label: 'Quote Pending', color: 'warning' },
  Declined: { label: 'Quote Declined', color: 'error' },
  Expired: { label: 'Quote Expired', color: 'error' },
  'No Quote': { label: 'No Quote', color: 'default' },
}

// warnOnly: only render when a quote exists and hasn't been accepted -
// the "chase this before the work runs" signal for planning surfaces
export const QuoteStatusChip = ({ status, warnOnly = false, sx }) => {
  if (!status) return null
  const display = QUOTE_STATUS_DISPLAY[status]
  if (!display) return null
  if (warnOnly && (display.accepted || status === 'No Quote')) return null

  return (
    <Tooltip
      title={
        display.accepted
          ? 'This booking has an accepted quote'
          : `A quote exists for this booking but has not been accepted (${status.toLowerCase()})`
      }
    >
      <Chip
        size="small"
        color={display.color}
        variant={display.accepted ? 'outlined' : 'filled'}
        icon={
          display.accepted ? (
            <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
          ) : (
            <WarningAmberIcon sx={{ fontSize: 14 }} />
          )
        }
        label={display.label}
        sx={{ borderRadius: 1, height: 20, fontSize: '0.65rem', fontWeight: 700, ...sx }}
      />
    </Tooltip>
  )
}

// Batched lookup: give it every booking id visible on the page, get back
// { [bookingId]: status }. Refetches only when the id set changes.
export const useBookingQuoteStatuses = (bookingIds) => {
  const [statuses, setStatuses] = useState({})

  const key = useMemo(
    () =>
      [...new Set((bookingIds || []).filter(Boolean).map(String))]
        .sort()
        .join(','),
    [bookingIds],
  )

  useEffect(() => {
    if (!key) {
      setStatuses({})
      return undefined
    }
    let cancelled = false
    API.get(`/pts/bookings/quote-statuses?ids=${key}`)
      .then((res) => {
        if (!cancelled && res.status === 200) setStatuses(res.data)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [key])

  return statuses
}
